import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { KeyRound, RefreshCw, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminGuides() {
  const [token, setToken] = useState(() => localStorage.getItem("guideReviewToken") || "");
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [openSlug, setOpenSlug] = useState(null);
  const [previewLang, setPreviewLang] = useState("en");

  const load = async (tk = token) => {
    setError("");
    try {
      const r = await fetch(`${API}/admin/articles`, { headers: { Authorization: `Bearer ${tk}` } });
      if (r.status === 401) { setError("Invalid review token."); setArticles(null); return; }
      if (!r.ok) { setError("Could not load pending guides."); return; }
      const data = await r.json();
      setArticles(data.articles || []);
    } catch {
      setError("Network error — try again.");
    }
  };

  const saveToken = () => {
    localStorage.setItem("guideReviewToken", token.trim());
    load(token.trim());
  };

  const act = async (slug, action) => {
    setBusy(slug + action);
    try {
      const r = await fetch(`${API}/admin/articles/${slug}/${action}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token.trim()}` }
      });
      if (r.ok) {
        setArticles((prev) => prev.filter((a) => a.slug !== slug));
        if (openSlug === slug) setOpenSlug(null);
      } else {
        setError("Action failed — check your token.");
      }
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6" data-testid="admin-guides-page">
      <Helmet>
        <title>Guide Review — 1st Nations Security</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="admin-title">Monthly Guides — Review Queue</h1>
      <p className="mt-3 text-sm text-slate-400">
        Auto-generated guides appear here before publishing. Approve to publish to /guides (both languages), or reject to discard. Nothing goes live without your approval.
      </p>

      <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-slate-800/70 bg-card/60 p-5 sm:flex-row sm:items-center">
        <KeyRound size={18} className="shrink-0 text-amber-400" />
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Review token"
          className="w-full rounded-full border border-slate-700 bg-obsidian/80 px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-500 focus:border-amber-500/60 focus:outline-none"
          data-testid="admin-token-input"
        />
        <button onClick={saveToken} className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-amber-500 px-6 py-2.5 text-sm font-bold text-obsidian transition-colors hover:bg-amber-300" data-testid="admin-load-button">
          <RefreshCw size={14} /> Load queue
        </button>
      </div>
      {error && <p className="mt-4 text-sm text-red-400" data-testid="admin-error">{error}</p>}

      {articles !== null && (
        <div className="mt-8 space-y-4" data-testid="pending-list">
          {articles.length === 0 && (
            <p className="rounded-2xl border border-slate-800/70 bg-card/60 p-8 text-center text-sm text-slate-400" data-testid="no-pending">
              No guides waiting for review. New ones arrive on the 1st of each month.
            </p>
          )}
          {articles.map((a) => (
            <div key={a.slug} className="rounded-2xl border border-slate-800/70 bg-card/60" data-testid={`pending-article-${a.slug}`}>
              <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <span className="rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300">{a.category.en}</span>
                  <h2 className="mt-2 font-display text-lg font-bold text-slate-100">{a.en.title}</h2>
                  <p className="mt-1 text-xs text-slate-500">{a.fr.title}</p>
                  <p className="mt-1 font-mono text-[11px] text-slate-600">/guides/{a.slug} · {a.date}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => { setOpenSlug(openSlug === a.slug ? null : a.slug); }}
                    className="flex items-center gap-1.5 rounded-full border border-slate-600 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-amber-500/50 hover:text-amber-300"
                    data-testid={`preview-${a.slug}`}
                  >
                    {openSlug === a.slug ? <EyeOff size={13} /> : <Eye size={13} />} Preview
                  </button>
                  <button
                    onClick={() => act(a.slug, "approve")}
                    disabled={busy}
                    className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-obsidian transition-colors hover:bg-emerald-400 disabled:opacity-50"
                    data-testid={`approve-${a.slug}`}
                  >
                    <CheckCircle2 size={13} /> Approve
                  </button>
                  <button
                    onClick={() => act(a.slug, "reject")}
                    disabled={busy}
                    className="flex items-center gap-1.5 rounded-full border border-red-500/50 px-4 py-2 text-xs font-semibold text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50"
                    data-testid={`reject-${a.slug}`}
                  >
                    <XCircle size={13} /> Reject
                  </button>
                </div>
              </div>
              {openSlug === a.slug && (
                <div className="border-t border-slate-800/70 p-6" data-testid={`preview-body-${a.slug}`}>
                  <div className="mb-4 flex gap-2">
                    {["en", "fr"].map((l) => (
                      <button key={l} onClick={() => setPreviewLang(l)} className={`rounded-full px-4 py-1.5 text-xs font-semibold ${previewLang === l ? "bg-amber-500 text-obsidian" : "border border-slate-700 text-slate-300"}`} data-testid={`preview-lang-${l}`}>
                        {l === "en" ? "English" : "Français"}
                      </button>
                    ))}
                  </div>
                  <h3 className="font-display text-xl font-bold">{a[previewLang].title}</h3>
                  <p className="mt-2 text-sm italic text-slate-400">{a[previewLang].excerpt}</p>
                  {a[previewLang].sections.map((s, i) => (
                    <div key={i} className="mt-5">
                      <h4 className="font-display text-base font-bold text-amber-300">{s.h2}</h4>
                      {s.paras.map((p, j) => (
                        <p key={j} className="mt-2 text-sm leading-relaxed text-slate-300">{p}</p>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
